import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { copyWebsitePages } from 'src/editor/api/website-websites-api'
import { useFetchAndSwitchPage } from 'src/editor/hooks/use-fetch-and-switch-page'
import { CreatePageModal } from 'src/editor/layout/sidebar/pages-management/components/create-page-modal'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import {
  GhostIconButton,
  SecondaryButton,
} from 'src/editor/shared/ui-lib/buttons'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import {
  BadgeColorEnum,
  BadgeSizeEnum,
} from 'src/editor/shared/ui-lib/shared/badge-consts'
import { getLocalesCountByPageSets } from 'src/editor/shared/utils/get-locales-count-by-page-sets'
import { setCurrentWebsiteLocale } from 'src/editor/store/website-settings/website-settings-actions'
import {
  getCurrentWebsiteLocale,
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { DuplicatePagesBanner } from './components/duplicate-pages-banner'
import { PageElementWithActions } from './components/page-element-with-actions'

export const PagesManagement = () => {
  const { t } = useTranslation()

  const [isCreatePageModalVisible, setCreatePageModalVisible] = useState(false)

  const dispatch = useAppDispatch()

  const websiteLocale = useWebsiteSettings(getWebsiteLocale) as PageLocaleEnum
  const currentWebsiteLocale = useWebsiteSettings(
    getCurrentWebsiteLocale,
  ) as PageLocaleEnum
  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocales = useWebsiteSettings(getWebsiteLocales) || []

  const { data, isFetching, isRefetching } = websitePagesQuery.useQuery({
    queryFnArgs: [websiteId],
  })

  const localesCount = useMemo(
    () => getLocalesCountByPageSets(data?.pages ?? []),
    [data?.pages],
  )

  const localeOptions = useMemo(
    () =>
      getLocalesOptions({
        t,
        localeList: websiteLocales,
        setAdditionalProperties: locale => ({
          optionBadgeProps: {
            color: BadgeColorEnum.Primary,
            size: BadgeSizeEnum.Small,
            children: t(
              'website_settings.sidebar.pages_management.language_switcher.badge_option',
              {
                count: localesCount[locale] ?? 0,
              },
            ),
          },
        }),
      }),
    [localesCount, t, websiteLocales],
  )

  const { fetcher: fetcherCopyPages, isLoading: isLoadingCopyPages } =
    useMutationWithValidation({
      requestFunction: copyWebsitePages,
      successToastMessage: t('website_settings.copy_pages.copied_successfully'),
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  const {
    urlPageId,
    isLoading: isPageFetching,
    checkPageModification,
    renderUnsavedPageModal,
  } = useFetchAndSwitchPage()

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="inline-flex basis-full items-center gap-3">
          <span className="text-heading-4">{t('pages_management.title')}</span>
          <LanguageSelector
            withoutWrapper
            className="basis-full"
            selectedLocales={currentWebsiteLocale}
            options={localeOptions}
            onChange={locale =>
              dispatch(setCurrentWebsiteLocale(locale as PageLocaleEnum))
            }
          />
        </div>
        <GhostIconButton
          onClick={() => setCreatePageModalVisible(true)}
          icon={<PlusCircleIcon />}
          title={t('pages_management.create_page')}
        />
      </div>
      <div className="flex flex-col gap-1">
        {isFetching && !data?.pages?.length && (
          <Skeleton height={64} count={3} />
        )}
        {data?.pages?.length &&
          data.pages.map(({ id, pageType, locales }) => {
            const websitePage = locales.find(
              ({ locale }) => locale === currentWebsiteLocale,
            )
            return (
              websitePage && (
                <PageElementWithActions
                  key={id}
                  hasSkeleton={isPageFetching || isRefetching}
                  websiteId={websiteId}
                  pageType={pageType}
                  websitePageSetId={id}
                  websitePageId={websitePage.id}
                  name={websitePage?.name ?? ''}
                  pageId={websitePage.pageContentId}
                  isPublished={websitePage?.active ?? false}
                  isActive={urlPageId === websitePage.pageContentId.toString()}
                  onClick={() =>
                    checkPageModification(websitePage.pageContentId)
                  }
                />
              )
            )
          })}
        {!isFetching && !localesCount[currentWebsiteLocale] && (
          <div className="flex flex-col gap-3">
            <span className="text-M text-textTertiary">
              {t('pages_management.no_pages_in_language', {
                language: t(localeNames[websiteLocale]),
              })}
            </span>
            <SecondaryButton
              isLoading={isLoadingCopyPages}
              onClick={() => {
                fetcherCopyPages({
                  id: websiteId,
                  locale: websiteLocale,
                })
              }}
            >
              {t('pages_management.copy_pages.button')}
            </SecondaryButton>
          </div>
        )}
      </div>
      <CreatePageModal
        isVisible={isCreatePageModalVisible}
        onClose={() => setCreatePageModalVisible(false)}
        websiteId={websiteId}
        currentWebsiteLocale={currentWebsiteLocale}
      />
      <DuplicatePagesBanner
        localesCount={localesCount}
        buttonProps={{
          isLoading: isLoadingCopyPages,
          onClick: () => {
            fetcherCopyPages({ id: websiteId, locale: currentWebsiteLocale })
          },
        }}
      />
      {renderUnsavedPageModal()}
    </>
  )
}
